import { z } from 'zod';
import { MOVEMENT_SOURCES } from './movement.js';
import { createCommentTypeSchema } from './comment.js';
/**
 * Tris offerts par l'écran.
 *
 * Liste **fermée** : c'est elle qui autorise le tri côté base. Un nom de
 * colonne venu de l'extérieur n'entre jamais dans la requête.
 *
 * `impact` n'est pas une colonne : c'est l'ordre **par défaut** de l'écran —
 * les références qui pèsent le plus d'abord, leurs lignes groupées. Personne
 * n'ouvre le Restant pour lire soixante-dix mille lignes par date : on vient
 * chercher ce qui compte, et un écran qui l'affiche d'emblée évite un réglage
 * à chaque visite.
 */
export const TRIS_RESTANT = [
    'impact',
    'cote',
    'source',
    'codpro',
    'jour',
    'reference',
    'motif',
    'uvc',
];
export const SENS_TRI = ['ASC', 'DESC'];
/** Valeur du filtre multi-couples qui désigne les mouvements sans motif. */
export const FILTRE_SANS_MOTIF = 'SANS_MOTIF';
export const listRestantQuerySchema = z.object({
    page: z.coerce.number().int().min(1).default(1),
    pageSize: z.coerce.number().int().min(10).max(500).default(100),
    cote: z.enum(['WMS', 'DOUANE']).optional(),
    source: z.enum(MOVEMENT_SOURCES).optional(),
    /**
     * Un ou plusieurs codes articles, séparés par des espaces (ou des virgules) :
     * « 433592 433289 433487 ». On instruit rarement un article seul — les
     * mouvements qui s'expliquent l'un l'autre se répartissent sur plusieurs
     * codes, et les saisir un par un obligerait à recommencer la sélection.
     */
    codpros: z
        .union([z.string(), z.array(z.string())])
        .transform((valeur) => (Array.isArray(valeur) ? valeur : valeur.split(/[\s,;]+/))
        .map((code) => code.trim())
        .filter((code) => code.length > 0))
        .default([]),
    /**
     * Quantité recherchée, **en valeur absolue** : saisir 480 rend les +480 comme
     * les −480. C'est le geste naturel quand on cherche la contrepartie d'une
     * ligne — elle porte le même nombre, au signe près.
     */
    uvc: z.coerce.number().optional(),
    du: z
        .string()
        .regex(/^\d{4}-\d{2}-\d{2}$/, 'Date attendue au format AAAA-MM-JJ')
        .optional(),
    au: z
        .string()
        .regex(/^\d{4}-\d{2}-\d{2}$/, 'Date attendue au format AAAA-MM-JJ')
        .optional(),
    sens: z.enum(['ENTREE', 'SORTIE']).optional(),
    /**
     * Couples « code/motif » (ex. `20/SCA`), combinés en OU ; `FILTRE_SANS_MOTIF`
     * isole les mouvements sans motif. Le couple fait foi : `20/SCA` ne ramène
     * pas `30/SCA` (lot 0.14.0).
     */
    motifs: z
        .union([z.string(), z.array(z.string())])
        .transform((valeur) => (Array.isArray(valeur) ? valeur : valeur.split(','))
        .map((couple) => couple.trim())
        .filter((couple) => couple.length > 0))
        .default([]),
    /**
     * Les ODE sont **affichés par défaut** (arbitrage Sam) : ce sont eux qu'on
     * vient chercher ici. La case les masque, elle ne les révèle pas.
     */
    masquerOde: z
        .union([z.boolean(), z.enum(['true', 'false'])])
        .transform((v) => (typeof v === 'boolean' ? v : v === 'true'))
        .default(false),
    /**
     * Périmètre 3A-bis (N19) : par défaut, côté WMS, seuls les mouvements qui
     * pèsent sur une clé suivie contrôlée ; côté douane, tout, toujours. Vrai :
     * tout est affiché. Même filtre que l'écran Écarts.
     */
    inclureHorsControle: z
        .union([z.boolean(), z.enum(['true', 'false'])])
        .transform((v) => (typeof v === 'boolean' ? v : v === 'true'))
        .default(false),
    search: z.string().trim().max(120).optional(),
    /** Tri. Par défaut l'impact : le plus lourd d'abord, groupé par référence. */
    triPar: z.enum(TRIS_RESTANT).default('impact'),
    triSens: z.enum(SENS_TRI).default('DESC'),
});
/**
 * Modification d'un type de rapprochement : libellé, activation, et
 * « propose la validation ».
 */
export const updateReconciliationTypeSchema = z
    .object({
    libelle: createCommentTypeSchema.shape.libelle.optional(),
    actif: z.boolean().optional(),
    proposeValidation: z.boolean().optional(),
})
    .refine((v) => v.libelle !== undefined || v.actif !== undefined || v.proposeValidation !== undefined, { message: 'Rien à modifier' });
/** Aperçu d'une sélection : le type choisi décide de la proposition. */
export const resumeSelectionSchema = z.object({
    mouvementIds: z.array(z.string().uuid()).default([]),
    typeId: z.string().uuid().optional(),
});
export const RECONCILIATION_STATUSES = ['ACTIF', 'ANNULE'];
/**
 * Filtre de l'historique des rapprochements, partagé par la liste et par son
 * export : ce qu'on exporte est exactement ce qu'on voit (lot 0.14.0).
 * `types` : identifiants de types, combinés en OU, et avec le statut en ET.
 */
export const listRapprochementsQuerySchema = z.object({
    statut: z.enum(RECONCILIATION_STATUSES).optional(),
    types: z
        .union([z.string(), z.array(z.string())])
        .transform((valeur) => (Array.isArray(valeur) ? valeur : valeur.split(','))
        .map((id) => id.trim())
        .filter((id) => id.length > 0))
        .pipe(z.array(z.string().uuid('Type inconnu')))
        .default([]),
});
export const RECONCILIATION_CONFLICTS = [
    'AUCUN',
    'APPARIEMENT',
    'JOURNEE_REMPLACEE',
    'QUANTITE_MODIFIEE',
];
export const RECONCILIATION_CONFLICT_LABELS = {
    AUCUN: '—',
    APPARIEMENT: 'Une contrepartie automatique est arrivée depuis',
    JOURNEE_REMPLACEE: 'Une journée réimportée a retiré un mouvement rapproché',
    QUANTITE_MODIFIEE: 'La quantité d’un mouvement rapproché a changé depuis',
};
export const createRapprochementSchema = z.object({
    /**
     * Identifiants des agrégats sélectionnés. Au moins deux, sauf pour un type
     * qui « propose la validation » : la règle dépend du type, l'API la tient.
     */
    mouvementIds: z.array(z.string().uuid()).min(1, 'Sélectionnez au moins un mouvement'),
    typeId: z.string({ required_error: 'Type de rapprochement requis' }).uuid('Type inconnu'),
    commentaire: z
        .string({ required_error: 'Le commentaire est obligatoire' })
        .trim()
        .min(1, 'Le commentaire est obligatoire')
        .max(2000),
    /**
     * Confirmation explicite d'un net non nul. Sans elle, un rapprochement qui
     * laisse un résiduel est refusé : on ne laisse pas passer par distraction ce
     * qui n'a pas été expliqué.
     */
    residuelConfirme: z.boolean().default(false),
});
export const annulerRapprochementSchema = z.object({
    motif: z
        .string({ required_error: "Le motif d'annulation est obligatoire" })
        .trim()
        .min(10, "Le motif d'annulation doit être explicite (10 caractères au minimum)")
        .max(2000),
});
export const acquitterConflitSchema = z.object({
    motif: z.string().trim().min(1, 'Dites pourquoi le rapprochement est conservé').max(2000),
});
//# sourceMappingURL=restant.js.map